/**
 * Pure helpers for the purchase-order PDF (no DOM): grouping lines by
 * brand, money, page packing and the copyable text layer. Kept apart from
 * the page builder so they can be unit-tested.
 */
import type { PurchaseItem } from "../../services/procurementService";
import type { ProductImage } from "../../types/productImage";

export interface PoLine {
    /** Running number in the printed order (1, 2, 3…), across brands. */
    no: number;
    item: PurchaseItem;
    /** Ordered quantity × unit cost (gifts are 0). */
    lineTotal: number;
}

export interface BrandGroup {
    /** The brand's name, or null for products without one. */
    brand: string | null;
    lines: PoLine[];
    subtotal: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** A line's ordered value: its total cost, else quantity × unit cost. */
export const lineTotalOf = (item: PurchaseItem): number =>
    item.is_gift
        ? 0
        : round2(
              item.total_cost != null && !Number.isNaN(Number(item.total_cost))
                  ? Number(item.total_cost)
                  : Number(item.quantity_ordered) * Number(item.unit_cost),
          );

// Placeholder brand names that mean "no brand" (printed under "Others") (the storefront treats them
// the same way, lib/utils.ts getDisplayBrand).
const PLACEHOLDER_BRANDS = new Set(["no brand", "not specified", "none", "n/a", "na", "unknown", "generic"]);

/** The brand to group a line under, or null for none. */
export const brandOf = (item: PurchaseItem): string | null => {
    const name = item.product?.brand?.name?.trim();
    return name && !PLACEHOLDER_BRANDS.has(name.toLowerCase()) ? name : null;
};

/**
 * Lines grouped under their brand (A→Z, products without a brand last),
 * keeping the order's own order inside each brand. Numbers run 1, 2, 3…
 * down the printed list.
 */
export function groupByBrand(items: PurchaseItem[]): BrandGroup[] {
    const byBrand = new Map<string | null, PurchaseItem[]>();
    items.forEach((item) => {
        const brand = brandOf(item);
        byBrand.set(brand, [...(byBrand.get(brand) ?? []), item]);
    });
    const brands = Array.from(byBrand.keys()).sort((a, b) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return a.localeCompare(b, "en", { sensitivity: "base" });
    });
    let no = 0;
    return brands.map((brand) => {
        const lines = (byBrand.get(brand) ?? []).map((item) => ({ no: ++no, item, lineTotal: lineTotalOf(item) }));
        return { brand, lines, subtotal: round2(lines.reduce((sum, l) => sum + l.lineTotal, 0)) };
    });
}

/** The photo a product shows first: its default image, else the first in order. */
export function primaryImage(images: ProductImage[] | undefined): ProductImage | null {
    if (!images?.length) return null;
    return [...images].sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.display_order - b.display_order)[0];
}

/** "1,000", "246.50" — decimals only when the amount has paisa. */
export function formatMoney(value: number): string {
    const n = round2(Number(value) || 0);
    const whole = Number.isInteger(n);
    return n.toLocaleString("en-US", {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
    });
}

export interface PackBlock {
    height: number;
    /** Must share a page with the block after it (a brand band and its first row). */
    keepWithNext?: boolean;
}

/**
 * Splits blocks over pages greedily: page 1 holds `firstCapacity` pixels,
 * later pages `nextCapacity`. A block that keeps with the next one moves
 * with it; a block taller than a whole page still gets a page of its own.
 * Returns the block indexes on each page.
 */
export function packPages(blocks: PackBlock[], firstCapacity: number, nextCapacity: number): number[][] {
    const pages: number[][] = [[]];
    let used = 0;
    let capacity = firstCapacity;
    blocks.forEach((block, i) => {
        const needed = block.height + (block.keepWithNext && blocks[i + 1] ? blocks[i + 1].height : 0);
        const page = pages[pages.length - 1];
        if (page.length > 0 && used + needed > capacity) {
            pages.push([]);
            used = 0;
            capacity = nextCapacity;
        }
        pages[pages.length - 1].push(i);
        used += block.height;
    });
    return pages;
}

/**
 * Text for the invisible, copyable layer. The PDF's built-in font only
 * covers Latin-1, so the taka sign becomes "Tk", common punctuation is
 * simplified, and anything else (e.g. Bangla) is left out — it still
 * shows in the page image.
 */
export function toCopyableText(text: string): string {
    const out = text
        .replace(/৳\s?/g, "Tk ")
        .replace(/[–—−]/g, "-")
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/…/g, "...")
        .replace(/[•·]/g, "·")
        .replace(/\s+/g, " ")
        .replace(/[^ -ÿ]/g, "")
        .trim();
    // Nothing readable left (a Bangla line leaves only its commas): skip it.
    return /[A-Za-z0-9\u00C0-\u00FF]/.test(out) ? out : "";
}
